import { Search, UserMinus, UserPlus, Users } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { EmptyState } from '@/components/common/States'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmModal } from '@/components/ui/modal'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { fmtDate } from '../../shared/format'
import { useRemoveRoleUser } from '../api'
import type { Role } from '../types'
import { useAccessLookups } from '../useLookups'
import { toastError } from '../ui'

export function RoleUsersTable({ role, canWrite, onAssign }: { role: Role; canWrite: boolean; onAssign: () => void }) {
  const { t } = useTranslation()
  const { userById, departmentById } = useAccessLookups()
  const remove = useRemoveRoleUser()
  const [q, setQ] = useState('')

  const rows = useMemo(() => {
    const term = q.trim().toLocaleLowerCase('tr')
    return role.assignments
      .map((a) => ({ ...a, user: userById.get(a.userId) }))
      .filter((r) => !term || `${r.user?.name} ${r.user?.email}`.toLocaleLowerCase('tr').includes(term))
  }, [role.assignments, userById, q])

  const onRemove = async (userId: string) => {
    const ok = await confirmModal({
      title: t('access.users.removeTitle'),
      description: t('access.users.removeBody', { name: userById.get(userId)?.name ?? '', role: role.name }),
      confirmLabel: t('access.users.remove'),
      destructive: true,
    })
    if (!ok) return
    try {
      await remove.mutateAsync({
        id: role.id,
        userId,
        remainingUserIds: role.assignments.map((a) => a.userId).filter((id) => id !== userId),
      })
      toast.success(t('access.users.removed'))
    } catch (e) {
      toastError(e)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('access.users.search')} aria-label={t('access.users.search')} className="pl-8" />
        </div>
        {canWrite ? (
          <Button size="sm" variant="outline" onClick={onAssign}>
            <UserPlus />
            {t('access.roles.actions.assignUsers')}
          </Button>
        ) : null}
      </div>
      {role.assignments.length === 0 ? (
        <EmptyState icon={Users} title={t('access.users.empty')} />
      ) : (
        <div className="overflow-hidden rounded-xl border">
          <Table>
            <THead>
              <TR className="hover:bg-transparent">
                <TH>{t('access.users.columns.name')}</TH>
                <TH className="hidden md:table-cell">{t('access.users.columns.email')}</TH>
                <TH className="hidden lg:table-cell">{t('access.users.columns.department')}</TH>
                <TH className="hidden lg:table-cell">{t('access.users.columns.position')}</TH>
                <TH className="hidden sm:table-cell">{t('access.users.columns.assignedAt')}</TH>
                {canWrite ? <TH className="w-12" /> : null}
              </TR>
            </THead>
            <TBody>
              {rows.map((r) => (
                <TR key={r.userId}>
                  <TD>
                    <div className="flex min-w-0 items-center gap-2.5">
                      <Avatar name={r.user?.name ?? '?'} className="size-7 shrink-0" />
                      <div className="min-w-0">
                        <div className="whitespace-nowrap font-medium">{r.user?.name ?? '—'}</div>
                        <div className="truncate text-xs text-muted-foreground md:hidden">{r.user?.email}</div>
                      </div>
                    </div>
                  </TD>
                  <TD className="hidden text-muted-foreground md:table-cell">{r.user?.email}</TD>
                  <TD className="hidden lg:table-cell">{departmentById.get(r.user?.departmentId ?? '')?.name ?? '—'}</TD>
                  <TD className="hidden text-muted-foreground lg:table-cell">{r.user?.position}</TD>
                  <TD className="hidden sm:table-cell">
                    <div className="whitespace-nowrap tabular-nums">{fmtDate(r.assignedAt)}</div>
                    <div className="text-xs text-muted-foreground">{r.assignedBy}</div>
                  </TD>
                  {canWrite ? (
                    <TD className="text-right">
                      <Tooltip side="top" content={t('access.users.remove')}>
                        <Button size="icon" variant="ghost" className="size-8 text-muted-foreground hover:text-destructive" aria-label={t('access.users.remove')} disabled={remove.isPending} onClick={() => void onRemove(r.userId)}>
                          <UserMinus />
                        </Button>
                      </Tooltip>
                    </TD>
                  ) : null}
                </TR>
              ))}
            </TBody>
          </Table>
          {rows.length === 0 ? <p className="p-4 text-center text-sm text-muted-foreground">{t('common.empty')}</p> : null}
        </div>
      )}
    </div>
  )
}
